import { useSyncExternalStore } from "react";
import axios from "axios";
import { getServerErrorCode, getServerErrorPayload } from "../shared/axiosErrors";
import { clearOnSignOut } from "../utils/signOutCleanup";

/** ai_engine's 403 codes for a project it will never serve to this session, so every later
 * request fails the same way and retrying cannot help:
 * - `project_inactive`: the organization is marked inactive; its frozen database is never served.
 * - `tenant_not_provisioned`: the organization has no project database. */
export type ProjectRefusal = "project_inactive" | "tenant_not_provisioned";
const PROJECT_REFUSALS: readonly ProjectRefusal[] = ["project_inactive", "tenant_not_provisioned"];

let refusal: ProjectRefusal | null = null;
const listeners = new Set<() => void>();

/** The refusal a failed request carries, by its error-code header or its `detail`; null for any
 * other failure, including every other 403. */
export function projectRefusalOf(error: unknown): ProjectRefusal | null {
    if (!axios.isAxiosError(error) || error.response?.status !== 403) return null;
    const code = getServerErrorCode(error);
    const detail = getServerErrorPayload(error)?.detail;
    return PROJECT_REFUSALS.find((known) => known === code || known === detail) ?? null;
}

function publish(next: ProjectRefusal | null): void {
    if (refusal === next) return;
    refusal = next;
    listeners.forEach((listener) => listener());
}

/** Lasts until the person signs out: the refusal belongs to that session. */
export function markProjectRefused(next: ProjectRefusal): void {
    publish(next);
}

export function clearProjectRefusal(): void {
    publish(null);
}

clearOnSignOut(clearProjectRefusal);

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function useProjectRefusal(): ProjectRefusal | null {
    return useSyncExternalStore(subscribe, () => refusal);
}
